import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import RightSlideModal from '../../components/RightSlideModal';
import CommonTable, { TableRowAvatarCell } from '../../components/CommonTable';

export default function ClassesPage() {
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSession, setSelectedSession] = useState('');
  const [sessions, setSessions] = useState([]);
  const [formData, setFormData] = useState({ className: '', description: '', sessionYear: '2024-2025' });
  const [sectionData, setSectionData] = useState({ sectionName: '', classId: '' });

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const [classesRes, sessionsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/academic-sessions')
      ]);

      const classesData = classesRes.data;
      setClasses(classesData);
      setSessions(sessionsRes.data);

      if (sessionsRes.data.length > 0 && !selectedSession) {
        setSelectedSession(sessionsRes.data[sessionsRes.data.length - 1].sessionYear);
      }

      const sectionsMap = {};
      for (const cls of classesData) {
        const sectionsResponse = await api.get(`/sections?classId=${cls.id}`);
        sectionsMap[cls.id] = sectionsResponse.data;
      }
      setSections(sectionsMap);
    } catch (error) {
      toast.error('Failed to fetch classes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedClass) {
        await api.put(`/classes/${selectedClass.id}`, formData);
        toast.success('Class updated successfully');
      } else {
        await api.post('/classes', formData);
        toast.success('Class created successfully');
      }
      setShowModal(false);
      setFormData({ className: '', description: '', sessionYear: '2024-2025' });
      setSelectedClass(null);
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save class');
    }
  };

  const handleDelete = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await api.delete(`/classes/${classId}`);
      toast.success('Class deleted successfully');
      fetchClasses();
    } catch (error) {
      toast.error('Failed to delete class');
    }
  };

  const handleAddSection = async (e) => {
    e.preventDefault();
    try {
      await api.post('/sections', sectionData);
      toast.success('Section added successfully');
      setShowSectionModal(false);
      setSectionData({ sectionName: '', classId: '' });
      fetchClasses();
    } catch (error) {
      toast.error('Failed to add section');
    }
  };

  const filteredClasses = classes.filter(c => c.sessionYear === selectedSession);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-outfit font-semibold text-2xl text-slate-800">Class Management</h2>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-slate-500">Managing classes for session </p>
            <select
              value={selectedSession}
              onChange={(e) => {
                setSelectedSession(e.target.value);
                setFormData(prev => ({ ...prev, sessionYear: e.target.value }));
              }}
              className="bg-indigo-50 text-indigo-700 font-semibold px-2 py-0.5 rounded-lg border-none focus:ring-2 focus:ring-indigo-100 outline-none cursor-pointer"
            >
              <option value="">Select Session</option>
              {sessions.map(s => <option key={s.id} value={s.sessionYear}>{s.sessionYear}</option>)}
            </select>
          </div>
        </div>
        <button
          data-testid="add-class-button"
          onClick={() => {
            setFormData({ className: '', description: '', sessionYear: selectedSession });
            setShowModal(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-2.5 font-medium shadow-sm active:scale-95 transition-all flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Class
        </button>
      </div>

      <CommonTable headers={['Class Name', 'Session', 'Description', 'Sections', 'Actions']}>
        {filteredClasses.length > 0 ? (
          filteredClasses.map((cls, index) => {
            const colors = [
              'bg-indigo-600/90 text-white',
              'bg-teal-600/90 text-white',
              'bg-amber-600/90 text-white',
              'bg-pink-600/90 text-white',
              'bg-rose-600/90 text-white',
              'bg-cyan-600/90 text-white',
              'bg-emerald-600/90 text-white'
            ];
            const colorClass = colors[index % colors.length];
            return (
              <tr key={cls.id} className="hover:bg-slate-50/50 transition-colors">
                <td className="px-6 py-4">
                  <TableRowAvatarCell 
                    title={cls.className} 
                    subtitle={cls.description ? (cls.description.substring(0, 30) + (cls.description.length > 30 ? '...' : '')) : ''}
                    colorClass={colorClass}
                  />
                </td>
                <td className="px-6 py-4">
                  <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-full">
                    {cls.sessionYear}
                  </span>
                </td>
                <td className="px-6 py-4 text-slate-500 text-sm max-w-xs truncate">
                  {cls.description || <span className="text-slate-300 italic">No description</span>}
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1.5 items-center max-w-sm">
                    {sections[cls.id]?.length > 0 ? (
                      sections[cls.id].map((section) => (
                        <span key={section.id} className="px-2.5 py-0.5 bg-slate-100 text-slate-700 text-xs font-semibold rounded-full border border-slate-200/50">
                          {section.sectionName}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400 italic">No sections</span>
                    )}
                    <button
                      data-testid={`add-section-${cls.id}`}
                      onClick={() => {
                        setSectionData({ sectionName: '', classId: cls.id });
                        setShowSectionModal(true);
                      }}
                      className="text-[9px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded font-bold uppercase hover:bg-indigo-600 hover:text-white transition-colors"
                    >
                      + Add
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`edit-class-${cls.id}`}
                      onClick={() => {
                        setSelectedClass(cls);
                        setFormData({ className: cls.className, description: cls.description || '', sessionYear: cls.sessionYear });
                        setShowModal(true);
                      }}
                      className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(cls.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan="5" className="text-center py-12 text-slate-500">No classes found for session {selectedSession}</td>
          </tr>
        )}
      </CommonTable>

      {/* Add/Edit Class Modal */}
      <RightSlideModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title={selectedClass ? 'Edit Class' : 'Add New Class'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Class Name</label>
            <input
              data-testid="class-name-input"
              type="text"
              value={formData.className}
              onChange={(e) => setFormData({ ...formData, className: e.target.value })}
              className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none"
              rows="3"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Session Year</label>
            <select
              value={formData.sessionYear}
              onChange={(e) => setFormData({ ...formData, sessionYear: e.target.value })}
              className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none"
              required
            >
              <option value="">Select Session</option>
              {sessions.map(s => <option key={s.id} value={s.sessionYear}>{s.sessionYear}</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-2.5 font-medium">
              {selectedClass ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-slate-200 hover:bg-slate-50 rounded-full py-2.5 font-medium">
              Cancel
            </button>
          </div>
        </form>
      </RightSlideModal>

      {/* Add Section Modal */}
      <RightSlideModal 
        isOpen={showSectionModal} 
        onClose={() => setShowSectionModal(false)} 
        title="Add Section"
      >
        <form onSubmit={handleAddSection} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Section Name</label>
            <input
              data-testid="section-name-input"
              type="text"
              value={sectionData.sectionName}
              onChange={(e) => setSectionData({ ...sectionData, sectionName: e.target.value })}
              placeholder="e.g., Section A"
              className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none"
              required
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-2.5 font-medium">
              Add Section
            </button>
            <button type="button" onClick={() => setShowSectionModal(false)} className="flex-1 border border-slate-200 hover:bg-slate-50 rounded-full py-2.5 font-medium">
              Cancel
            </button>
          </div>
        </form>
      </RightSlideModal>
    </div>
  );
}
